import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { Flame, Loader2 } from "lucide-react";
import api from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MediaToggle from "@/components/MediaToggle";
import MovieCard from "@/components/MovieCard";
import DetailModal from "@/components/DetailModal";
import SearchTorrents from "@/components/SearchTorrents";
import HotInForums from "@/components/HotInForums";
import useSeo from "@/hooks/useSeo";

const CATEGORY_LABELS = {
  popular: { label: "Popular", icon: "🔥" },
  top_rated: { label: "Top Rated", icon: "⭐" },
  now_playing: { label: "Now Playing", icon: "🎬" },
  upcoming: { label: "Upcoming", icon: "🗓" },
  "trending-week": { label: "Trending This Week", icon: "📈" },
  "trending-day": { label: "Trending Today", icon: "⚡" },
  "movies-tv": { label: "Movies + TV", icon: "🌐" },
  discover: { label: "Discover", icon: "🔍" },
  search: { label: "Search Results", icon: "🔎" },
};

const TV_CATEGORY_MAP = {
  popular: "popular",
  top_rated: "top_rated",
  now_playing: "on_the_air",
  upcoming: "airing_today",
};

export default function HomePage() {
  const [sp, setSp] = useSearchParams();
  const { mediaType: deepMediaType, tmdbId: deepTmdbId } = useParams();
  const navigate = useNavigate();
  const isDeepLink = Boolean(deepMediaType && deepTmdbId);
  const [mediaType, setMediaType] = useState(sp.get("type") || "movie");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(parseInt(sp.get("page") || "1", 10));
  const [active, setActive] = useState(null);
  const [genres, setGenres] = useState([]);

  const cat = sp.get("cat") || "popular";
  const q = sp.get("q") || "";
  const genre = sp.get("genre") || "";
  const year = sp.get("year") || "";
  const sort = sp.get("sort") || "popularity.desc";
  const provider = sp.get("provider") || "";
  const network = sp.get("network") || "";
  const company = sp.get("company") || "";

  const onMediaChange = (mt) => {
    setMediaType(mt);
    const next = new URLSearchParams(sp);
    next.set("type", mt);
    next.delete("page");
    setSp(next);
    setPage(1);
  };

  // Load genres
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/genres/${mediaType}`);
        setGenres(data);
      } catch {
        setGenres([]);
      }
    })();
  }, [mediaType]);

  // Load items
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        let data;
        if (cat === "search" && q) {
          const r = await api.get("/search", { params: { q, page } });
          data = r.data;
        } else if (cat === "discover" || genre || year || provider || network || company) {
          const r = await api.get(`/discover/${mediaType}`, {
            params: {
              page,
              genre: genre || undefined,
              year: year || undefined,
              sort_by: sort,
              provider: provider || undefined,
              network: (mediaType === "tv" ? (network || undefined) : undefined),
              company: (mediaType === "movie" ? (company || undefined) : undefined),
            },
          });
          data = r.data;
        } else if (cat === "trending-week") {
          const r = await api.get("/trending", { params: { media_type: mediaType, window: "week" } });
          data = r.data;
        } else if (cat === "trending-day") {
          const r = await api.get("/trending", { params: { media_type: mediaType, window: "day" } });
          data = r.data;
        } else if (cat === "movies-tv") {
          const r = await api.get("/trending", { params: { media_type: "all", window: "week" } });
          data = r.data;
        } else {
          const targetCat = mediaType === "tv" ? (TV_CATEGORY_MAP[cat] || "popular") : cat;
          const r = await api.get(`/${mediaType === "tv" ? "tv" : "movies"}/${targetCat}`, { params: { page } });
          data = r.data;
        }
        if (cancel) return;
        setItems(data.results || []);
        setTotalResults(data.total_results || (data.results || []).length);
        setTotalPages(Math.min(data.total_pages || 1, 500));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [cat, q, genre, year, sort, mediaType, page, provider, network, company]);

  // Open modal when arriving via /title/:mediaType/:tmdbId deep link
  useEffect(() => {
    if (isDeepLink) {
      setActive({
        id: Number(deepTmdbId),
        media_type: deepMediaType,
        title: "",
      });
    }
  }, [isDeepLink, deepMediaType, deepTmdbId]);

  const closeModal = () => {
    setActive(null);
    if (isDeepLink) navigate("/", { replace: true });
  };

  // Inject ItemList JSON-LD for Google rich-results on the homepage only
  useEffect(() => {
    const onHomepage = cat === "popular" && page === 1 && !q && !genre && !year;
    const SCRIPT_ID = "ld-json-itemlist";
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();

    if (!onHomepage || items.length === 0) return;

    const origin = window.location.origin;
    const top = items.slice(0, 10);
    const listType = mediaType === "tv" ? "TVSeries" : "Movie";
    const ld = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `Popular ${mediaType === "tv" ? "TV Shows" : "Movies"} on TorHub`,
      numberOfItems: top.length,
      itemListElement: top.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": listType,
          name: it.title || it.name,
          url: `${origin}/title/${it.media_type || mediaType}/${it.id}`,
          image: it.poster || it.poster_path,
          aggregateRating: it.rating
            ? {
                "@type": "AggregateRating",
                ratingValue: it.rating,
                bestRating: 10,
                ratingCount: 1,
              }
            : undefined,
          datePublished: it.year || undefined,
        },
      })),
    };

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(SCRIPT_ID);
      if (el) el.remove();
    };
  }, [cat, page, q, genre, year, items, mediaType]);

  const headerLabel = useMemo(() => {
    if (cat === "search") return `Search: "${q}"`;
    if (cat === "discover" && genre) {
      const g = genres.find((x) => String(x.id) === String(genre));
      return g ? `${g.name}` : "Discover";
    }
    return CATEGORY_LABELS[cat]?.label || "Popular";
  }, [cat, q, genre, genres]);

  const headerIcon = CATEGORY_LABELS[cat]?.icon || "🔥";

  // Per-route SEO: unique title, description, canonical, OG tags
  const seoTitle = q
    ? `Search “${q}” — TorHub`
    : (() => {
        const map = {
          popular: "Popular Movies & TV Torrents",
          top_rated: "Top Rated Movies & TV Torrents",
          now_playing: "Now Playing — Movie Torrents",
          upcoming: "Upcoming Movies — Torrents",
          "trending-week": "Trending This Week — Torrents",
          "trending-day": "Trending Today — Torrents",
          "movies-tv": "Movies & TV — All Torrents",
          discover: "Discover Movies & TV Torrents",
        };
        const base = map[cat] || "Movies & TV Torrents";
        return `${headerLabel} — ${base} | TorHub`;
      })();
  const seoDescription = q
    ? `Search results for “${q}” on TorHub. Discover movie and TV torrents with TMDB-powered posters, codec badges, and community reviews.`
    : `Browse ${headerLabel.toLowerCase()} movie and TV torrents on TorHub — TMDB-powered metadata, posters, codec badges, real-time seeder counts and community reviews. Magnets from YTS, EZTV, 1337x, TorrentGalaxy and more.`;
  useSeo({
    title: seoTitle,
    description: seoDescription,
    path: window.location.pathname + window.location.search,
  });

  const goPage = (p) => {
    const next = new URLSearchParams(sp);
    next.set("page", String(p));
    setSp(next);
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-black text-white">
      <Sidebar genres={genres} />
      <div className="flex-1 h-full overflow-y-auto flex flex-col">
        <Header genres={genres} />
        <MediaToggle value={mediaType} onChange={onMediaChange} />

        <div className="px-4 md:px-8 mt-2 mb-6 flex-1">
          {cat === "popular" && page === 1 && !q && !genre && !year && (
            <HotInForums />
          )}

          {cat === "search" && q && (
            <SearchTorrents q={q} />
          )}

          <div className="flex items-center gap-3 mb-5">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2" data-testid="home-h1">
              <span className="text-[var(--tt-primary)]">
                <Flame className="w-5 h-5 inline" />
              </span>
              {headerLabel}
              <span className="sr-only"> movie and TV torrents on TorHub</span>
            </h1>
            {!loading && (
              <span className="font-mono-tt text-[11px] bg-[var(--tt-surface-2)] border border-[var(--tt-border)] px-2 py-1 rounded text-[var(--tt-text-muted)]">
                {items.length} of {totalResults.toLocaleString()}
              </span>
            )}
          </div>

          {/* SEO intro — visible on first popular-listing page only, hidden from SR if duplicated */}
          {cat === "popular" && page === 1 && !q && !genre && (
            <p className="text-sm text-[var(--tt-text-muted)] max-w-3xl mb-6 leading-relaxed" data-testid="home-intro">
              TorHub is a community-driven discovery layer for movie and TV torrents. Every title is paired with
              TMDB-powered metadata — posters, ratings, runtimes, genres and trailers — while magnet links are
              aggregated live from YTS, EZTV, 1337x, TorrentGalaxy, Knaben, ext.to and more. Real-time seeder counts,
              codec/quality badges, a weekly Hall of Fame and an honest list of <a href="/software" className="text-[var(--tt-primary)] hover:underline">BitTorrent clients</a> keep the experience fast and ad-free.
            </p>
          )}

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6" data-testid="poster-skeleton-grid">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl aspect-[2/3] tt-skeleton tt-skeleton-poster"
                  style={{ animationDelay: `${(i % 5) * 0.12}s` }}
                />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center text-[var(--tt-text-muted)] py-12">
              {cat === "search"
                ? <>No TMDB metadata matched <span className="text-white/80">&ldquo;{q}&rdquo;</span>. Live torrent results (if any) are listed above.</>
                : "No results. Try a different filter or search."}
            </div>
          ) : (
            <>
              <div
                data-testid="movie-grid"
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
              >
                {items.map((it) => (
                  <MovieCard key={`${it.media_type}-${it.id}`} item={it} onOpen={setActive} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => goPage(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    data-testid="pagination-prev"
                    className="px-4 py-2 rounded-full bg-[var(--tt-surface-2)] hover:bg-[#222] text-xs font-semibold disabled:opacity-40"
                  >
                    ← Prev
                  </button>
                  <span className="text-xs font-mono-tt text-[var(--tt-text-muted)] px-3">
                    Page {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => goPage(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages}
                    data-testid="pagination-next"
                    className="px-4 py-2 rounded-full bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-xs font-semibold disabled:opacity-40"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        <Footer />
      </div>

      <DetailModal item={active} onClose={closeModal} />
    </div>
  );
}
